import { AfterViewInit, Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { AppService, DCO, DCOAdmin, GetDCOAdminsRequest, SnowflakeDco } from '../../app.service';
import { LoadingService } from '../../common/loading.service';
import { AlertService } from '../../common/alert.service';
import { catchError, finalize, of, tap } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { DcoAddEditDialogComponent } from './dco-add-edit-dialog/dco-add-edit-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatSort, Sort } from '@angular/material/sort';

@Component({
  selector: 'app-dco-admin',
  templateUrl: './dco-admin.component.html',
  styleUrl: './dco-admin.component.scss'
})
export class DcoAdminComponent implements OnInit, AfterViewInit {

  @ViewChild(MatSort) sort: MatSort;

  public showImported = false;
  
  public snowflakeDCOs: SnowflakeDco[] = [];
  public snowflakeDCOsDataSource: MatTableDataSource<SnowflakeDco> = null;
  public snowflakeDCOsDataColumns: string[] = ['imported', 'name', 'displayName', 'active', 'memberCount', 'ccos', 'actions'];


  constructor(
    private service: AppService,
    private alert: AlertService,
    private spinner: LoadingService,
    private dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    this.getDCOs();
  }

  ngAfterViewInit() {
    // Set sort only after the data is assigned to the dataSource
    if (this.snowflakeDCOsDataSource) {
      this.snowflakeDCOsDataSource.sort = this.sort;
    }
  }

  private getDCOs() {
    const request = new GetDCOAdminsRequest();

    // Show the spinner before the request starts
    this.spinner.show();

    this.service.GetDCOAdmins(request).pipe(
      tap((result) => {
        if (result.success) {
          this.snowflakeDCOs = result.dcosInSnowflake;
          this.snowflakeDCOsDataSource = new MatTableDataSource(result.dcosInSnowflake);
          this.updateFilteredData(); // Update data source initially
        } else {
          console.log(`ERROR: ${result.message}`);
        }
      }),
      catchError((error) => {
        console.error(error);
        return of([]); // Return an empty array or appropriate fallback in case of error
      }),
      finalize(() => {
        // Hide the spinner after the request completes, whether it was successful or not
        this.spinner.hide();
      })
    ).subscribe();
  }

  openAddEditDCODialog(dcoToAddEdit: SnowflakeDco) {
    let dcoToSave = new DCO();
    if (dcoToAddEdit.savedDco) {
      dcoToSave = dcoToAddEdit.savedDco;
    } else {
      dcoToSave.name = dcoToAddEdit.name;
    }
    const dialogRef = this.dialog.open(DcoAddEditDialogComponent, { data: { dcoToSave: dcoToSave, isEditMode: dcoToAddEdit.savedDco ? true : false } })
      .afterClosed()
      .subscribe(response => {
        if (response?.data != null) {
          if (response.data.refresh) {
            this.getDCOs();
          }
        }
      });
  }

  applyFilter() {
    this.updateFilteredData(); // Update the filtered data when checkbox state changes
  }

  private updateFilteredData() {
    // Filter based on `showImported` checkbox value
    const filteredData = this.showImported
      ? this.snowflakeDCOs.filter(dco => dco.savedDco != null)
      : this.snowflakeDCOs;

    // Update the data source with filtered data
    this.snowflakeDCOsDataSource.data = filteredData;

    // Ensure sort is applied after data is updated
    if (this.sort) {
      this.snowflakeDCOsDataSource.sort = this.sort;
    }
  }

  public sortData(sort: Sort) {
    const data = this.snowflakeDCOs.slice();
    if (!sort.active || sort.direction === '') {
      this.snowflakeDCOsDataSource.data = data;
      return;
    }

    this.snowflakeDCOsDataSource.data = this.snowflakeDCOsDataSource.data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'imported': return this.compare(a.savedDco ? true : false, b.savedDco ? true : false, isAsc);
        case 'active': return this.compare(a.savedDco ? a.savedDco.active : false, b.savedDco ? b.savedDco.active : false, isAsc);
        case 'name': return this.compare(a.name, b.name, isAsc);
        case 'displayName': return this.compare(a.savedDco ? a.savedDco.displayName : '', b.savedDco ? b.savedDco.displayName : '', isAsc);
        case 'memberCount': return this.compare(a.activeMemberCount, b.activeMemberCount, isAsc);
        default: return 0;
      }
    });
  }

  public compare(a: number | string | Date | boolean, b: number | string | Date | boolean, isAsc: boolean) {
    if (typeof a === 'boolean' && typeof b === 'boolean') {
      return (a === b ? 0 : a ? 1 : -1) * (isAsc ? 1 : -1);
    }

    if (typeof a === 'string' && typeof b === 'string') {
      return a.localeCompare(b) * (isAsc ? 1 : -1);
    }

    return ((a < b ? -1 : 1) * (isAsc ? 1 : -1));
  }


}
